import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, from, of } from "rxjs";
import { map } from "rxjs/operators";
import {
  Firestore,
  collectionData,
  collection,
  CollectionReference,
  doc,
  docData,
  setDoc,
  addDoc
} from "@angular/fire/firestore";
import { orderBy, query } from "@firebase/firestore";

import { Recording } from "../../models/recordings/recordings.interface";
import { Transcript, TranscriptNote } from "../../models/transcripts/transcripts.interface";
import { getFunctions, httpsCallable, HttpsCallableResult } from "firebase/functions";

@Injectable({
  providedIn: "root",
})
export class RecordingService {
  recording$: Observable<Recording[]>;

  constructor(private firestore: Firestore) {}

  public getRecordings(): Observable<Recording[]> {
    const notesRef = collection(this.firestore, "recordings");
    return collectionData(notesRef, { idField: "id" }) as Observable<Recording[]>;
  }

  public getRecordings2(): Observable<Recording[]> {
    return collectionData<Recording>(
      query<Recording>(
        collection(this.firestore, "recordings") as CollectionReference<Recording>,
        orderBy("id")
      ),
      { idField: "id" }
    );
  }



  public getRecordingById(id: string): Observable<Recording> {
    const bookRef = doc(this.firestore, `recordings/${id}`);
    return docData(bookRef, { idField: 'id' }) as Observable<Recording>;
  }


  public getTranscriptById(id: string): Observable<Transcript> {
    const bookRef = doc(this.firestore, `transcripts/${id}`);
    return docData(bookRef, { idField: 'id' }) as Observable<Transcript>;
  }


  public async getRawTranscript(transcript: Transcript) {

    if (transcript.results != undefined) {
      console.log('getRawTranscript Results', transcript.id)
      return transcript.results;
    } else {

      const functions = getFunctions();
      console.log('getRawTranscript', transcript.id)
      const getRawTranscript = httpsCallable(functions, 'getRawTranscript');
      const rawTrans = (await getRawTranscript({transcriptId: transcript.id})).data
      return rawTrans;
    }
  }



public addTranscriptNote(id: string, note: TranscriptNote) {
  console.log('addTranscriptNote', id, note)
  const functions = getFunctions();

  const addTranscriptNote = httpsCallable(functions, 'addTranscriptNote');

  const params = {
    transcriptId: id,
    transcriptNote: note
  }

  return addTranscriptNote(params)

}

public getTranscriptNotes(recordingId : string): Observable<TranscriptNote[]> {
  console.log('getTranscriptNotes', recordingId)
  return collectionData<TranscriptNote>(
    query<TranscriptNote>(
      collection(this.firestore, "transcripts", recordingId, "notes") as CollectionReference<TranscriptNote>,
      orderBy("creationTime")
    ),
    { idField: "id" }
  );
}


  public saveRecording(recording: Recording): string {

    var id = this.firestoreAutoId();
    const bookRef = doc(this.firestore, `recordings/${id}`);
    setDoc(bookRef, recording);

    return id
  }


  firestoreAutoId = (): string => {
    const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  
    let autoId = ''
  
    for (let i = 0; i < 20; i++) {
      autoId += CHARS.charAt(
        Math.floor(Math.random() * CHARS.length)
      )
    }
    return autoId
  }

  public reprocessRecording(recordingId: string) {
    const functions = getFunctions();

    const reprocessRecording = httpsCallable(functions, 'reprocessRecording');

    console.log('reprocessRecording', recordingId)
    return reprocessRecording({force:true, recordingId}); 
  }


  public async getRecordingAiResults(transcriptId: string): Promise<any> {
    console.log('getRecordingAiResults', transcriptId);

    const functions = getFunctions();
    const getAiResults = httpsCallable(functions, 'getAiResults');

    const result = await getAiResults({ transcriptId });

    return result.data;
  }

  public getRecordingAiRecordingScoring(transcriptId: string): Observable<any> {
    const functions = getFunctions();

    const getAiReportResults = httpsCallable<
      { transcriptId: string; type: string },
      any
    >(functions, 'getAiResults');

    return from(
      getAiReportResults({
        transcriptId,
        type: 'recordingScoring'
      })
    ).pipe(
      map((result: HttpsCallableResult<any>) => result.data)
    );
  }
  
  public async getRecordingAiReportResults2(transcriptId: string): Promise<any> {
    console.log('getRecordingAiReportResults', transcriptId);

    const functions = getFunctions();
    const getAiReportResults = httpsCallable(functions, 'getAiResults');

    const result = await getAiReportResults({ transcriptId: transcriptId, type:'recordingReport' });

    return result.data;
  }

  public getRecordingAiReportResults(transcriptId: string): Observable<any> {
    const functions = getFunctions();

    const getAiReportResults = httpsCallable<
      { transcriptId: string; type: string },
      any
    >(functions, 'getAiResults');

    return from(
      getAiReportResults({
        transcriptId,
        type: 'recordingReport'
      })
    ).pipe(
      map((result: HttpsCallableResult<any>) => result.data)
    );
  }

  public async reprocessRecordingScoring(activityId: string, recordingId: string, sguid: string): Promise<any> {
    console.log('reprocessRecordingScoring', recordingId);

    const functions = getFunctions();
    const reprocessRecordingScoring = httpsCallable(functions, 'reprocessRecordingScoring');

    const result = await reprocessRecordingScoring({ activityId: activityId, recordingId: recordingId, sguid: sguid });

    return result.data;
  }
  
  public async reprocessRecordingReport(activityId: string, recordingId: string): Promise<any> {
    console.log('reprocessRecordingScoring', recordingId);

    const functions = getFunctions();
    const reprocessRecordingReport = httpsCallable(functions, 'reprocessRecordingReport');

    const result = await reprocessRecordingReport({ activityId: activityId, recordingId: recordingId });

    return result.data;
  }

  /**
   * SME review workbook (.xlsx) for a scored v2 recording activity. The backend returns
   * it inline as base64 (nothing is stored) and allows only Praxus Admins and the
   * activity agency's Agency Admins.
   */
  public async exportSmeReview(userId: string, skillId: string, activityId: string): Promise<{ filename: string; contentBase64: string }> {
    const functions = getFunctions();
    const exportSmeReview = httpsCallable<any, { filename: string; contentBase64: string }>(functions, 'exportSmeReview');

    const result = await exportSmeReview({ userId, skillId, activityId });

    return result.data;
  }

}
